import React, { useState } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { RegularButton } from 'fronton-react';
import { CalculatorIcon } from '@fronton/icons-react';
import { useTranslation } from 'react-i18next';
import { isCalculatorAvailable } from 'features/product-info/ducks/selectors';
import { CALCULATE_QUANTITY_GA } from 'features/ga-events/messages/ga-calculate-quantity-open.pdp';
import { RootReducer } from 'store/root-reducer';
import { getArticle } from 'common/selectors';
import { BasketFlowActions, BasketItemFlow } from 'features/basket';
import { createGetExperiment } from 'features/page-model';
import { ConnectedProps } from 'types/utils';
import { HIDE_CALCULATOR_INTERNAL_ID } from 'common/constants';
import { PdpCalculatorModal } from '../pdp-calculator-modal';
import { DATA_TESTID_CALCULATE_QUANTITY_BUTTON } from './constants';
import { calculateQuantityButton } from './styles';

export type CalculateQuantityMappedState = {
  isCalcAvailable: boolean;
  productId: string | null;
};

export type CalculateQuantityProps = ConnectedProps<CalculateQuantityMappedState, typeof mapDispatchToProps>;

export const CalculateQuantity: React.FC<CalculateQuantityProps> = ({
  isCalcAvailable,
  productId,
  gaCalculateQuantityOpen,
  syncProductItemWithCookie,
}) => {
  const { t } = useTranslation();
  const [isShowCalculatorModal, setIsShowCalculatorModal] = useState<boolean>(false);

  const handleToggleModal = () => {
    syncProductItemWithCookie(productId, false);
    setIsShowCalculatorModal((prev) => !prev);
    if (!isShowCalculatorModal) {
      gaCalculateQuantityOpen();
    }
  };

  let content: React.ReactNode = null;

  if (isCalcAvailable) {
    content = (
      <div data-testid="calculate-quantity_mf-pdp">
        <RegularButton
          data-testid={DATA_TESTID_CALCULATE_QUANTITY_BUTTON}
          size="l"
          variant="secondary"
          iconLeft={<CalculatorIcon />}
          label={t('calculator.ui.calculate')}
          className={calculateQuantityButton}
          onClick={handleToggleModal}
        >
          {t('calculator.ui.calculate')}
        </RegularButton>
        <PdpCalculatorModal isShowed={isShowCalculatorModal} onCloseModal={handleToggleModal} />
      </div>
    );
  }

  return <>{content}</>;
};

export const mapStoreToProps = (store: RootReducer): CalculateQuantityMappedState => {
  const isCalcHidden = createGetExperiment(HIDE_CALCULATOR_INTERNAL_ID)(store);
  const isCalcAvailable = (isCalculatorAvailable(store) && !isCalcHidden) ?? false;
  const productId = getArticle(store);

  return {
    isCalcAvailable,
    productId,
  };
};

export const mapDispatchToProps = (dispatch: Dispatch) => ({
  gaCalculateQuantityOpen: () => {
    dispatch({ type: CALCULATE_QUANTITY_GA });
  },
  syncProductItemWithCookie: (productId: BasketItemFlow['productId'], isNeedGA?: boolean) =>
    dispatch(BasketFlowActions.SYNC_ITEM_WITH_COOKIE({ productId, isNeedGA })),
});

export const PdpCalculateQuantity = connect(mapStoreToProps, mapDispatchToProps)(CalculateQuantity);
